import { WorkerRequirementDomain } from "@clipboard-health/contract-worker-app-bff";
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { APP_V2_USER_EVENTS } from "@src/appV2/lib/analytics/events";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import { useSearchParams } from "@src/appV2/lib/utils/useSearchParams";
import { SystemInlineBanner, Text } from "@src/appV2/redesign/system/ui-components";
import { useMemo, useRef } from "react";
import { useParams } from "react-router-dom";

import { ListLoadingState } from "../../components/ListLoadingState";
import { PageWrapper } from "../../components/PageWrapper";
import { StickyScrollPageHeader } from "../../components/StickyScrollPageHeader";
import { PullToRefresh } from "../../ShiftDiscovery/ListView/PullToRefresh";
import { useScrollToTopOnStatusTap } from "../../useScrollToTopOnStatusTap";
import { useGetWorkplaceProfile } from "../../Workplace/useGetWorkplaceProfile";
import { useGetRequirementsForWorkplace } from "../api/wrappers/useGetRequirementsForWorkplace";
import { DocumentCardsWrapper } from "../components/DocumentCardsWrapper";
import { DocumentsPageDocumentCards } from "../DocumentsPage/DocumentCards";
import { DocumentsErrorPage } from "../ErrorPage";
import {
  WORKPLACE_DOCUMENTS_POST_BOOKING_REQUIREMENTS_FILTER_PARAM,
  type WorkplaceDocumentsPostBookingRequirementsFilter,
} from "../paths";
import { type WorkplaceDocumentsPageParams } from "../types";
import { useCredentialRequirementConfiguration } from "../useCredentialRequirementConfiguration";
import { useGetWorkerLicensesAllowedForDocuments } from "../useGetWorkerLicensesAllowedForDocuments";
import { filterPostBookingRequirementsForShift } from "../utils/filterPostBookingRequirementsForShift";
import { PostBookingSupportBanner } from "./PostBookingSupportBanner";

interface WorkplaceDocumentsSearchParams extends Record<string, unknown> {
  qualification?: string;
  domain?: WorkerRequirementDomain;
  includeCredentials?: string;
  shiftId?: string;
  [WORKPLACE_DOCUMENTS_POST_BOOKING_REQUIREMENTS_FILTER_PARAM]?: string;
}

function parsePostBookingFilter(
  value: string | undefined
): WorkplaceDocumentsPostBookingRequirementsFilter | undefined {
  return value === "exclude" || value === "include-only" ? value : undefined;
}

export function WorkplaceDocumentsPage() {
  const { workplaceId } = useParams<WorkplaceDocumentsPageParams>();
  const {
    qualification,
    domain = WorkerRequirementDomain.FLEX,
    includeCredentials,
    shiftId,
    [WORKPLACE_DOCUMENTS_POST_BOOKING_REQUIREMENTS_FILTER_PARAM]: rawPostBookingFilter,
  } = useSearchParams<WorkplaceDocumentsSearchParams>();

  const postBookingRequirementsFilter = parsePostBookingFilter(rawPostBookingFilter);

  const credentialRequirementConfiguration = useCredentialRequirementConfiguration();

  const { licenses: availableLicenses } = useGetWorkerLicensesAllowedForDocuments();

  const stackRef = useRef<HTMLDivElement>(null);
  useScrollToTopOnStatusTap(stackRef);

  const {
    data: workplaceProfile,
    isLoading: isWorkplaceProfileLoading,
    isError: isWorkplaceProfileError,
  } = useGetWorkplaceProfile({
    workplaceId,
  });

  const {
    data: workerRequirements,
    isLoading: isWorkerRequirementsLoading,
    isError: isWorkerRequirementsError,
    refetch,
  } = useGetRequirementsForWorkplace({
    qualifications: qualification ? [qualification] : availableLicenses,
    domain,
    workplaceId,
    postBookingRequirementsFilter,
  });

  useLogEffect(APP_V2_USER_EVENTS.VIEWED_WORKPLACE_DOCUMENTS, {
    workplace_id: workplaceId,
    qualification,
  });

  const filteredWorkerRequirements =
    includeCredentials === "true"
      ? workerRequirements
      : workerRequirements?.filter(
          (workerRequirement) =>
            !credentialRequirementConfiguration.has(workerRequirement.requirementId)
        );

  const pendingPostBookingRequirements = useMemo(
    () =>
      filterPostBookingRequirementsForShift({
        workplace: {
          id: workplaceId,
          workplaceType: workplaceProfile?.type,
          msa: workplaceProfile?.fullAddress?.metropolitanStatisticalArea,
          state: workplaceProfile?.fullAddress?.state,
          region: workplaceProfile?.fullAddress?.region,
        },
        qualifications: qualification ? [qualification] : availableLicenses,
        workplaceRequirements: filteredWorkerRequirements,
      }) ?? [],
    [
      workplaceId,
      workplaceProfile?.type,
      workplaceProfile?.fullAddress?.metropolitanStatisticalArea,
      workplaceProfile?.fullAddress?.state,
      workplaceProfile?.fullAddress?.region,
      qualification,
      availableLicenses,
      filteredWorkerRequirements,
    ]
  );

  if (isWorkplaceProfileLoading || isWorkerRequirementsLoading) {
    return <ListLoadingState texts={["Loading workplace documents..."]} />;
  }

  if (isWorkplaceProfileError || isWorkerRequirementsError) {
    return <DocumentsErrorPage title="Error loading documents" />;
  }

  const shouldShowPostBookingSupportBanner =
    isDefined(shiftId) &&
    postBookingRequirementsFilter !== "exclude" &&
    pendingPostBookingRequirements.length > 0;

  const headerPrefix =
    postBookingRequirementsFilter === "include-only"
      ? "Complete the remaining paperwork"
      : "Showing all required documents";
  const shiftNoun = domain === WorkerRequirementDomain.FLEX ? "shifts" : "jobs";

  const arePostBookingRequirementsSubmitted =
    postBookingRequirementsFilter === "include-only" &&
    isDefined(qualification) &&
    pendingPostBookingRequirements.length === 0;

  return (
    <PageWrapper>
      <StickyScrollPageHeader
        shouldWrapTitle
        title={workplaceProfile?.name}
        scrollContainerRef={stackRef}
      />
      {isDefined(qualification) && !arePostBookingRequirementsSubmitted && (
        <Box sx={{ paddingX: 5, paddingBottom: 4 }}>
          <Text>
            {headerPrefix} for {qualification} {shiftNoun} at this workplace
          </Text>
        </Box>
      )}

      {arePostBookingRequirementsSubmitted && (
        <SystemInlineBanner
          color="success"
          iconType="checkmark-filled"
          sx={{ marginX: 5, marginBottom: 4 }}
        >
          You&apos;re all set! All documents for {qualification} {shiftNoun} at this workplace have
          been submitted.
        </SystemInlineBanner>
      )}

      {shouldShowPostBookingSupportBanner && (
        <PostBookingSupportBanner
          workplaceId={workplaceId}
          shiftId={shiftId}
          qualification={qualification}
        />
      )}

      <PullToRefresh
        scrollRef={stackRef}
        onRefresh={async () => {
          await refetch();
        }}
      />

      <DocumentCardsWrapper ref={stackRef} sx={{ padding: 5 }}>
        <DocumentsPageDocumentCards documents={filteredWorkerRequirements} />
      </DocumentCardsWrapper>
    </PageWrapper>
  );
}
